<template>
  <div class="flex-child">
    <div ref="chartContainer" class="vue-chart">
      <el-skeleton :loading="soldChartDataStatusInjected === AsyncDataStatus.PENDING" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart
            v-if="soldChartDataStatusInjected === AsyncDataStatus.SUCCESS"
            width="100%"
            height="300"
            type="area"
            :options="chartOptions"
            :series="seriesList"
          />
        </template>
      </el-skeleton>
    </div>
    <div class="flex-text">
      <p v-html="$t('dashboardRoiGraphDescription')"></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataModel, ChartLabelsModel, ChartSeriesModel } from '~/types/apiService';
import type { AsyncDataRequestStatus } from '#app';
import { AsyncDataStatus } from '~/utils/constants/asyncData';
import { BigNumber } from 'bignumber.js';
import apexChartLocales from '~/utils/constants/apexChartLocales';
import type { ApexOptions } from 'apexcharts';
import { Formatters } from '~/utils/formatters/formatters';

const { t, locale } = useI18n();
const chartId = 'dex-chart-roi';
const chartContainer = ref<HTMLElement | null>(null);

const { data: soldData } = useNuxtData<ChartDataModel>('statistics/sold');
const soldChartDataStatusInjected = inject<Ref<AsyncDataRequestStatus> | AsyncDataStatus>(
  'soldChartDataStatus',
  AsyncDataStatus.IDLE
);

const roiGraphSeries = computed(() => {
  const series = soldData.value?.series?.filter((serie: ChartSeriesModel) => serie.name.includes('ReturnOfInvestment'));

  return {
    dates: soldData.value?.dates,
    series
  } as ChartDataModel;
});

const labels = computed<ChartLabelsModel>(() => {
  const categories = soldData.value?.dates || [];

  return {
    type: 'datetime',
    categories,
    tickAmount: 10,
    labels: {
      rotate: 0
    }
  };
});

const seriesList = computed(() => {
  if (!roiGraphSeries.value || !roiGraphSeries.value.series) {
    return [];
  }

  return roiGraphSeries.value.series.map((serie: ChartSeriesModel) => {
    return {
      ...serie,
      name: t(serie.name)
    };
  });
});

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },
  chart: {
    id: chartId,
    class: 'vuechart-example',
    background: '#00000000',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    locales: apexChartLocales,
    defaultLocale: locale.value
  },
  title: {
    text: `${t('statsReturnOfInvestment')}`,
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  stroke: {
    curve: 'smooth'
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        title: {
          style: {
            fontSize: locale.value === 'ru' ? '22px' : '24px'
          }
        },
        xaxis: {
          labels: {
            rotate: -45
          }
        }
      }
    }
  ],
  dataLabels: {
    enabled: false
  },
  yaxis: {
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (value: number) {
        return (
          Formatters.number.renderString((f) => f.formatNumber(new BigNumber(value).multipliedBy(100), 2, 10)) + '%'
        );
      }
    }
  },
  colors: ['#8E64F6'],
  xaxis: labels.value,
  tooltip: {
    x: {
      format: 'dd/MM/yy'
    }
  }
}));

useApexChartUpdater({
  chartId,
  chartContainer,
  chartOptions: chartOptions as ComputedRef<ApexOptions>,
  seriesList,
  labels,
  extraDeps: [locale]
});
</script>

<style scoped lang="scss">
.vue-chart {
  min-height: 340px;
}
</style>
